/**
 * Admin copy for Configurações › **Atendimento e IA** (D631, Digital Central phase 0, doc 25 section 11) and for the AI provider
 * credentials in Integrações.
 *
 * Kept in its own file; `messages.ts` spreads it into the catalog and asserts that no id collides. Same four base languages, one entry
 * per id, so a missing translation is a compile error. What a screen shows for a CODE (a purpose, an engine, a language, a service, a
 * reason, a refusal) is not here: it is the central vocabulary (`@vello/vocabulary`, `slice-d631`). Neutral: no country, currency or
 * provider literal besides the product names the owner chose (OpenRouter).
 */
type CentralAiEntry = Readonly<{ pt: string; en: string; es: string; it: string }>;

export const CENTRAL_AI_DICT: Readonly<Record<string, CentralAiEntry>> = {
  'nav.centralAiSettings': { pt: 'Atendimento e IA', en: 'Support and AI', es: 'Atención e IA', it: 'Assistenza e IA' },
  'centralai.title': { pt: 'Atendimento e IA', en: 'Support and AI', es: 'Atención e IA', it: 'Assistenza e IA' },
  'centralai.subtitle': {
    pt: 'O assistente automático da Central, o dinheiro que ele pode devolver sozinho e a IA que ele usa, por país.',
    en: 'The Central’s automatic assistant, the money it may return on its own and the AI it runs on, by country.',
    es: 'El asistente automático de la Central, el dinero que puede devolver por sí solo y la IA que usa, por país.',
    it: 'L’assistente automatico della Centrale, il denaro che può restituire da solo e l’IA che usa, per paese.',
  },
  'centralai.explain': {
    pt: 'Toda mudança passa por quem propõe e por outra pessoa que aprova. Onde nada foi definido vale o lado seguro: nenhum reembolso automático, nenhuma forma de pagamento oferecida e nenhuma chamada de IA. As chaves ficam no cofre de Integrações, nunca aqui.',
    en: 'Every change goes through an author and a different approver. Where nothing is set, the safe side holds: no automatic refund, no payment option offered and no AI call. Keys live in the Integrations vault, never here.',
    es: 'Todo cambio pasa por quien propone y otra persona que aprueba. Donde no hay nada definido vale el lado seguro: ningún reembolso automático, ninguna forma de pago ofrecida y ninguna llamada de IA. Las claves están en la bóveda de Integraciones, nunca aquí.',
    it: 'Ogni modifica passa da chi propone e da un’altra persona che approva. Dove nulla è definito vale il lato sicuro: nessun rimborso automatico, nessun metodo di pagamento offerto e nessuna chiamata all’IA. Le chiavi stanno nel caveau di Integrazioni, mai qui.',
  },

  // ---- the overview by place ----
  'centralai.overview.title': { pt: 'Por país', en: 'By country', es: 'Por país', it: 'Per paese' },
  'centralai.overview.hint': {
    pt: 'A plataforma traz o padrão de todos os países; o país pode ter o seu. Dinheiro e pagamento só existem por país.',
    en: 'The platform carries the default of every country; a country can have its own. Money and payment exist only per country.',
    es: 'La plataforma trae el valor predeterminado de todos los países; el país puede tener el suyo. El dinero y el pago solo existen por país.',
    it: 'La piattaforma porta il valore predefinito di tutti i paesi; il paese può avere il proprio. Denaro e pagamento esistono solo per paese.',
  },
  'centralai.overview.platform.hint': { pt: 'Padrão para todos os países', en: 'Default for every country', es: 'Valor predeterminado para todos los países', it: 'Valore predefinito per tutti i paesi' },
  'centralai.overview.edit': { pt: 'Editar', en: 'Edit', es: 'Editar', it: 'Modifica' },
  'centralai.overview.set': { pt: 'Definir', en: 'Set', es: 'Definir', it: 'Definisci' },
  'centralai.overview.open': { pt: '{action}: {setting} ({place})', en: '{action}: {setting} ({place})', es: '{action}: {setting} ({place})', it: '{action}: {setting} ({place})' },
  'centralai.state.NOT_ALLOWED': { pt: 'Não se aplica', en: 'Not applicable', es: 'No aplica', it: 'Non si applica' },
  'centralai.state.UNSET': { pt: 'Não definido', en: 'Not set', es: 'No definido', it: 'Non definito' },
  'centralai.state.DEFAULT': { pt: 'Padrão', en: 'Default', es: 'Predeterminado', it: 'Predefinito' },
  'centralai.state.SET': { pt: 'Versão {n}', en: 'Version {n}', es: 'Versión {n}', it: 'Versione {n}' },
  'centralai.state.INHERITED': { pt: 'Herdado da plataforma (versão {n})', en: 'Inherited from the platform (version {n})', es: 'Heredado de la plataforma (versión {n})', it: 'Ereditato dalla piattaforma (versione {n})' },
  'centralai.state.FAIL_CLOSED': { pt: 'Bloqueado', en: 'Blocked', es: 'Bloqueado', it: 'Bloccato' },
  'centralai.state.pending': { pt: '{n} aguardando aprovação', en: '{n} awaiting approval', es: '{n} a la espera de aprobación', it: '{n} in attesa di approvazione' },
  'centralai.summary.unreadable': { pt: 'Valor ilegível', en: 'Unreadable value', es: 'Valor ilegible', it: 'Valore illeggibile' },
  'centralai.summary.failClosed': {
    pt: 'A versão não confere com o registro de governança; a regra está bloqueada.',
    en: 'The version does not match the governance record; the rule is blocked.',
    es: 'La versión no coincide con el registro de gobernanza; la regla está bloqueada.',
    it: 'La versione non corrisponde al registro di governance; la regola è bloccata.',
  },
  'centralai.summary.assistant': { pt: '{name} · {languages}', en: '{name} · {languages}', es: '{name} · {languages}', it: '{name} · {languages}' },
  'centralai.summary.caps': {
    pt: 'Até {order} por corrida ou pedido e {month} por cliente no mês',
    en: 'Up to {order} per ride or order and {month} per customer a month',
    es: 'Hasta {order} por viaje o pedido y {month} por cliente al mes',
    it: 'Fino a {order} per corsa o ordine e {month} per cliente al mese',
  },
  'centralai.summary.pay': { pt: '{service}: {methods}', en: '{service}: {methods}', es: '{service}: {methods}', it: '{service}: {methods}' },
  'centralai.summary.model': { pt: '{purpose}: {model}', en: '{purpose}: {model}', es: '{purpose}: {model}', it: '{purpose}: {model}' },
  'centralai.summary.speech.openrouter': { pt: 'Modelo de voz pelo OpenRouter', en: 'Speech model through OpenRouter', es: 'Modelo de voz por OpenRouter', it: 'Modello vocale tramite OpenRouter' },
  'centralai.summary.speech.self': { pt: 'Servidor próprio ({name})', en: 'Own server ({name})', es: 'Servidor propio ({name})', it: 'Server proprio ({name})' },
  'centralai.summary.budget': {
    pt: '{amount} por mês · avisos em {alerts}',
    en: '{amount} a month · alerts at {alerts}',
    es: '{amount} al mes · avisos en {alerts}',
    it: '{amount} al mese · avvisi a {alerts}',
  },

  // ---- the provider credentials seen from the settings ----
  'centralai.credentials.title': { pt: 'Chaves e servidores de IA', en: 'AI keys and servers', es: 'Claves y servidores de IA', it: 'Chiavi e server di IA' },
  'centralai.credentials.hint': {
    pt: 'A chave do OpenRouter e o endereço do servidor de voz próprio ficam no cofre de Integrações. Aqui só se vê se estão prontos.',
    en: 'The OpenRouter key and the own speech server address live in the Integrations vault. Here you only see whether they are ready.',
    es: 'La clave de OpenRouter y la dirección del servidor de voz propio están en la bóveda de Integraciones. Aquí solo se ve si están listos.',
    it: 'La chiave di OpenRouter e l’indirizzo del server vocale proprio stanno nel caveau di Integrazioni. Qui si vede solo se sono pronti.',
  },
  'centralai.credentials.unreadable': { pt: 'Não foi possível ler o estado das chaves agora.', en: 'The state of the keys could not be read right now.', es: 'No se pudo leer el estado de las claves ahora.', it: 'Impossibile leggere ora lo stato delle chiavi.' },
  'centralai.credentials.ready': { pt: 'Pronto', en: 'Ready', es: 'Listo', it: 'Pronto' },
  'centralai.credentials.off': { pt: 'Desligado', en: 'Off', es: 'Apagado', it: 'Spento' },
  'centralai.credentials.missing': { pt: 'Falta configurar', en: 'Not set up', es: 'Falta configurar', it: 'Da configurare' },
  'centralai.credentials.open': { pt: 'Abrir Integrações', en: 'Open Integrations', es: 'Abrir Integraciones', it: 'Apri Integrazioni' },

  // ---- editors ----
  'centralai.currency': { pt: 'Moeda', en: 'Currency', es: 'Moneda', it: 'Valuta' },
  'centralai.currency.hint': { pt: 'Código de três letras, por exemplo a moeda do país.', en: 'Three-letter code, for example the country’s currency.', es: 'Código de tres letras, por ejemplo la moneda del país.', it: 'Codice di tre lettere, ad esempio la valuta del paese.' },

  'centralai.assistant.name': { pt: 'Nome do assistente', en: 'Assistant name', es: 'Nombre del asistente', it: 'Nome dell’assistente' },
  'centralai.assistant.name.hint': { pt: 'Até 40 caracteres. Aparece para o cliente.', en: 'Up to 40 characters. The customer sees it.', es: 'Hasta 40 caracteres. El cliente lo ve.', it: 'Fino a 40 caratteri. Lo vede il cliente.' },
  'centralai.assistant.languages': { pt: 'Idiomas atendidos', en: 'Languages served', es: 'Idiomas atendidos', it: 'Lingue servite' },
  'centralai.assistant.languages.hint': {
    pt: 'O primeiro idioma é o reserva quando o cliente escreve em outro.',
    en: 'The first language is the fallback when the customer writes in another one.',
    es: 'El primer idioma es el de reserva cuando el cliente escribe en otro.',
    it: 'La prima lingua è quella di riserva quando il cliente scrive in un’altra.',
  },
  'centralai.assistant.disclosure': { pt: 'Frase de apresentação ({language})', en: 'Opening line ({language})', es: 'Frase de presentación ({language})', it: 'Frase di presentazione ({language})' },
  'centralai.assistant.disclosure.hint': {
    pt: 'Diz que é um assistente automático e que uma pessoa está a uma palavra de distância. De 20 a 500 caracteres.',
    en: 'Says it is an automatic assistant and that a person is one word away. 20 to 500 characters.',
    es: 'Dice que es un asistente automático y que una persona está a una palabra de distancia. De 20 a 500 caracteres.',
    it: 'Dice che è un assistente automatico e che una persona è a una parola di distanza. Da 20 a 500 caratteri.',
  },

  'centralai.caps.explain': {
    pt: 'O que o assistente pode devolver ou creditar sozinho. Acima disso uma pessoa decide. Sem versão publicada, ele não devolve nada sozinho.',
    en: 'What the assistant may return or credit on its own. Above that a person decides. With no published version it returns nothing on its own.',
    es: 'Lo que el asistente puede devolver o acreditar por sí solo. Por encima decide una persona. Sin versión publicada, no devuelve nada por sí solo.',
    it: 'Ciò che l’assistente può restituire o accreditare da solo. Oltre decide una persona. Senza versione pubblicata non restituisce nulla da solo.',
  },
  'centralai.caps.order': { pt: 'Limite por corrida ou pedido', en: 'Limit per ride or order', es: 'Límite por viaje o pedido', it: 'Limite per corsa o ordine' },
  'centralai.caps.order.hint': { pt: 'Em {currency}. Conta tudo o que o assistente já devolveu naquela corrida ou pedido.', en: 'In {currency}. Counts everything the assistant already returned on that ride or order.', es: 'En {currency}. Cuenta todo lo que el asistente ya devolvió en ese viaje o pedido.', it: 'In {currency}. Conta tutto ciò che l’assistente ha già restituito su quella corsa o ordine.' },
  'centralai.caps.month': { pt: 'Limite por cliente no mês', en: 'Limit per customer a month', es: 'Límite por cliente al mes', it: 'Limite per cliente al mese' },
  'centralai.caps.month.hint': { pt: 'Em {currency}. No mês do calendário, somando todas as devoluções automáticas ao cliente.', en: 'In {currency}. In the calendar month, adding up every automatic return to the customer.', es: 'En {currency}. En el mes calendario, sumando todas las devoluciones automáticas al cliente.', it: 'In {currency}. Nel mese di calendario, sommando tutte le restituzioni automatiche al cliente.' },
  'centralai.caps.inverted': { pt: 'O limite mensal não pode ser menor que o limite por corrida ou pedido.', en: 'The monthly limit cannot be below the per-ride or per-order limit.', es: 'El límite mensual no puede ser menor que el límite por viaje o pedido.', it: 'Il limite mensile non può essere inferiore a quello per corsa o ordine.' },

  'centralai.pay.explain': {
    pt: 'Formas de pagamento que a Central oferece ao fazer um pedido por conversa ou telefone, por serviço. Só vale pagar no fim da corrida ou na entrega; nunca cartão digitado na conversa. Sem formas definidas, a Central oferece só o pagamento feito no fim que não precisa de autorização antes (dinheiro).',
    en: 'Payment options the Central offers when taking an order by chat or phone, per service. Only payment at the end of the ride or on delivery counts; never a card typed into the conversation. With none set, the Central offers only payment made at the end that needs no authorization beforehand (cash).',
    es: 'Formas de pago que la Central ofrece al tomar un pedido por chat o teléfono, por servicio. Solo vale pagar al final del viaje o en la entrega; nunca tarjeta digitada en la conversación. Sin formas definidas, la Central ofrece solo el pago hecho al final que no necesita autorización previa (efectivo).',
    it: 'Metodi di pagamento che la Centrale offre prendendo un ordine in chat o al telefono, per servizio. Vale solo il pagamento a fine corsa o alla consegna; mai una carta digitata nella conversazione. Senza metodi definiti la Centrale offre solo il pagamento fatto alla fine che non richiede autorizzazione preventiva (contanti).',
  },
  'centralai.pay.noFrame': { pt: 'Este país ainda não cadastrou formas de pagamento. Cadastre-as antes de definir esta regra.', en: 'This country has not registered payment methods yet. Register them before setting this rule.', es: 'Este país aún no registró formas de pago. Regístralas antes de definir esta regla.', it: 'Questo paese non ha ancora registrato metodi di pagamento. Registrali prima di definire questa regola.' },
  'centralai.pay.unregistered': { pt: 'Há uma forma de pagamento marcada que o país não tem cadastrada. Desmarque-a para propor.', en: 'A marked payment option is not registered in this country. Unmark it to propose.', es: 'Hay una forma de pago marcada que el país no tiene registrada. Desmárcala para proponer.', it: 'Un metodo di pagamento selezionato non è registrato in questo paese. Deselezionalo per proporre.' },
  'centralai.pay.empty': { pt: 'Nenhum serviço ainda. Adicione corridas, comida ou envios.', en: 'No service yet. Add rides, food or Send.', es: 'Ningún servicio aún. Añade viajes, comida o envíos.', it: 'Nessun servizio ancora. Aggiungi corse, cibo o spedizioni.' },
  'centralai.pay.ride': { pt: 'Corridas: {category}', en: 'Rides: {category}', es: 'Viajes: {category}', it: 'Corse: {category}' },
  'centralai.pay.none': { pt: 'Nenhuma: a Central não oferece pagamento neste serviço', en: 'None: the Central offers no payment for this service', es: 'Ninguna: la Central no ofrece pago en este servicio', it: 'Nessuno: la Centrale non offre pagamento per questo servizio' },
  'centralai.pay.remove': { pt: 'Tirar serviço', en: 'Remove service', es: 'Quitar servicio', it: 'Rimuovi servizio' },
  'centralai.pay.add': { pt: 'Adicionar serviço', en: 'Add a service', es: 'Añadir servicio', it: 'Aggiungi servizio' },
  'centralai.pay.add.choose': { pt: 'Escolha um serviço', en: 'Choose a service', es: 'Elige un servicio', it: 'Scegli un servizio' },
  'centralai.pay.add.action': { pt: 'Adicionar', en: 'Add', es: 'Añadir', it: 'Aggiungi' },

  'centralai.models.explain': {
    pt: 'O modelo de cada finalidade, como o OpenRouter o escreve (fabricante/modelo), e um modelo reserva para quando o primeiro falhar. Finalidade sem modelo não chama a IA.',
    en: 'The model for each purpose, written the way OpenRouter names it (vendor/model), and a fallback model for when the first one fails. A purpose with no model makes no AI call.',
    es: 'El modelo de cada finalidad, como lo escribe OpenRouter (fabricante/modelo), y un modelo de respaldo para cuando falle el primero. Una finalidad sin modelo no llama a la IA.',
    it: 'Il modello di ogni finalità, scritto come lo chiama OpenRouter (produttore/modello), e un modello di riserva per quando il primo fallisce. Una finalità senza modello non chiama l’IA.',
  },
  'centralai.models.inherit': { pt: 'Em branco, vale o modelo do padrão da plataforma para aquela finalidade.', en: 'Left blank, the platform default model for that purpose applies.', es: 'En blanco, vale el modelo predeterminado de la plataforma para esa finalidad.', it: 'Se vuoto, vale il modello predefinito della piattaforma per quella finalità.' },
  'centralai.models.model': { pt: 'Modelo: {purpose}', en: 'Model: {purpose}', es: 'Modelo: {purpose}', it: 'Modello: {purpose}' },
  'centralai.models.fallback': { pt: 'Reserva: {purpose}', en: 'Fallback: {purpose}', es: 'Respaldo: {purpose}', it: 'Riserva: {purpose}' },
  'centralai.models.model.ph': { pt: 'fabricante/modelo', en: 'vendor/model', es: 'fabricante/modelo', it: 'produttore/modello' },

  'centralai.speech.engine': { pt: 'Motor de transcrição', en: 'Transcription engine', es: 'Motor de transcripción', it: 'Motore di trascrizione' },
  'centralai.speech.engine.hint': { pt: 'O modelo de voz do OpenRouter usa a finalidade “Transcrição de áudio” dos modelos. O servidor próprio é bem mais barato.', en: 'The OpenRouter speech model uses the “Audio transcription” purpose of the models. The own server is much cheaper.', es: 'El modelo de voz de OpenRouter usa la finalidad “Transcripción de audio” de los modelos. El servidor propio es mucho más barato.', it: 'Il modello vocale di OpenRouter usa la finalità “Trascrizione audio” dei modelli. Il server proprio costa molto meno.' },
  'centralai.speech.ref': { pt: 'Nome da credencial do servidor', en: 'Server credential name', es: 'Nombre de la credencial del servidor', it: 'Nome della credenziale del server' },
  'centralai.speech.ref.hint': { pt: 'Só o nome (letras minúsculas, números, - e _), o mesmo salvo em Integrações. Nunca o endereço nem a chave.', en: 'Only the name (lower-case letters, digits, - and _), the same saved in Integrations. Never the address or the key.', es: 'Solo el nombre (minúsculas, números, - y _), el mismo guardado en Integraciones. Nunca la dirección ni la clave.', it: 'Solo il nome (minuscole, numeri, - e _), lo stesso salvato in Integrazioni. Mai l’indirizzo né la chiave.' },
  'centralai.speech.slot.ready': { pt: 'Servidor pronto no cofre.', en: 'Server ready in the vault.', es: 'Servidor listo en la bóveda.', it: 'Server pronto nel caveau.' },
  'centralai.speech.slot.off': { pt: 'O servidor está desligado em Integrações.', en: 'The server is switched off in Integrations.', es: 'El servidor está apagado en Integraciones.', it: 'Il server è spento in Integrazioni.' },
  'centralai.speech.slot.missing': { pt: 'Esse nome ainda não existe em Integrações. Salve o servidor lá antes de aprovar.', en: 'That name does not exist in Integrations yet. Save the server there before approving.', es: 'Ese nombre aún no existe en Integraciones. Guarda el servidor allí antes de aprobar.', it: 'Quel nome non esiste ancora in Integrazioni. Salva lì il server prima di approvare.' },

  'centralai.budget.explain.platform': { pt: 'O teto de gasto com IA em todos os países somados, por mês.', en: 'The AI spending ceiling across all countries together, per month.', es: 'El tope de gasto en IA de todos los países juntos, por mes.', it: 'Il tetto di spesa in IA di tutti i paesi insieme, al mese.' },
  'centralai.budget.explain.country': { pt: 'O teto de gasto com IA deste país, por mês. O gasto é comparado a ele e ao total da plataforma.', en: 'The AI spending ceiling for this country, per month. Spend is compared with it and with the platform total.', es: 'El tope de gasto en IA de este país, por mes. El gasto se compara con él y con el total de la plataforma.', it: 'Il tetto di spesa in IA di questo paese, al mese. La spesa è confrontata con esso e con il totale della piattaforma.' },
  'centralai.budget.amount': { pt: 'Orçamento mensal', en: 'Monthly budget', es: 'Presupuesto mensual', it: 'Budget mensile' },
  'centralai.budget.amount.hint': { pt: 'Em {currency}, por mês do calendário.', en: 'In {currency}, per calendar month.', es: 'En {currency}, por mes calendario.', it: 'In {currency}, per mese di calendario.' },
  'centralai.budget.alerts': { pt: 'Avisar ao chegar em (%)', en: 'Alert at (%)', es: 'Avisar al llegar a (%)', it: 'Avvisa al raggiungimento di (%)' },
  'centralai.budget.alerts.hint': { pt: 'Porcentagens do orçamento separadas por vírgula, em ordem crescente. O padrão é 80 e 100.', en: 'Percentages of the budget separated by commas, ascending. The default is 80 and 100.', es: 'Porcentajes del presupuesto separados por comas, en orden creciente. El valor predeterminado es 80 y 100.', it: 'Percentuali del budget separate da virgole, in ordine crescente. Il predefinito è 80 e 100.' },

  // ---- Integrações › IA ----
  'aiint.heading': { pt: 'IA: OpenRouter e transcrição própria', en: 'AI: OpenRouter and own transcription', es: 'IA: OpenRouter y transcripción propia', it: 'IA: OpenRouter e trascrizione propria' },
  'aiint.hint': {
    pt: 'A chave do OpenRouter e o servidor de voz próprio da Central. As chaves são gravadas sem volta: depois só aparecem mascaradas. Qual modelo cada finalidade usa está em Configurações › Atendimento e IA.',
    en: 'The Central’s OpenRouter key and own speech server. Keys are stored write-only: afterwards they only show masked. Which model each purpose uses is in Settings › Support and AI.',
    es: 'La clave de OpenRouter y el servidor de voz propio de la Central. Las claves se guardan sin vuelta: después solo aparecen enmascaradas. Qué modelo usa cada finalidad está en Configuración › Atención e IA.',
    it: 'La chiave di OpenRouter e il server vocale proprio della Centrale. Le chiavi sono scritte senza ritorno: poi compaiono solo mascherate. Quale modello usa ogni finalità è in Impostazioni › Assistenza e IA.',
  },
  'aiint.openrouter': { pt: 'OpenRouter', en: 'OpenRouter', es: 'OpenRouter', it: 'OpenRouter' },
  'aiint.saved': { pt: 'Salvo.', en: 'Saved.', es: 'Guardado.', it: 'Salvato.' },
  'aiint.save.failed': { pt: 'Não foi possível salvar ({code}).', en: 'Could not save ({code}).', es: 'No se pudo guardar ({code}).', it: 'Impossibile salvare ({code}).' },
  'aiint.load.failed': { pt: 'Não foi possível ler as chaves de IA ({code}).', en: 'Could not read the AI keys ({code}).', es: 'No se pudieron leer las claves de IA ({code}).', it: 'Impossibile leggere le chiavi di IA ({code}).' },
  'aiint.speech.heading': { pt: 'Servidores de transcrição próprios', en: 'Own transcription servers', es: 'Servidores de transcripción propios', it: 'Server di trascrizione propri' },
  'aiint.speech.hint': {
    pt: 'Cada servidor tem um nome. Em Atendimento e IA, o país escolhe o servidor pelo nome; o endereço e a chave ficam só aqui.',
    en: 'Each server has a name. In Support and AI, the country picks the server by name; the address and the key stay only here.',
    es: 'Cada servidor tiene un nombre. En Atención e IA, el país elige el servidor por nombre; la dirección y la clave quedan solo aquí.',
    it: 'Ogni server ha un nome. In Assistenza e IA il paese sceglie il server per nome; l’indirizzo e la chiave restano solo qui.',
  },
  'aiint.speech.named': { pt: 'Servidor {name}', en: 'Server {name}', es: 'Servidor {name}', it: 'Server {name}' },
  'aiint.speech.new': { pt: 'Novo servidor de transcrição', en: 'New transcription server', es: 'Nuevo servidor de transcripción', it: 'Nuovo server di trascrizione' },
  'aiint.speech.name': { pt: 'Nome do servidor', en: 'Server name', es: 'Nombre del servidor', it: 'Nome del server' },
  'aiint.speech.name.hint': { pt: 'De 3 a 41 letras minúsculas, números, - ou _. É o nome que o país escolhe.', en: '3 to 41 lower-case letters, digits, - or _. It is the name the country picks.', es: 'De 3 a 41 minúsculas, números, - o _. Es el nombre que el país elige.', it: 'Da 3 a 41 minuscole, numeri, - o _. È il nome che il paese sceglie.' },
  'aiint.speech.key': { pt: 'Chave de acesso (opcional)', en: 'Access key (optional)', es: 'Clave de acceso (opcional)', it: 'Chiave di accesso (facoltativa)' },
  'aiint.speech.key.hint': { pt: 'Se o servidor exige uma. Gravada sem volta.', en: 'If the server requires one. Stored write-only.', es: 'Si el servidor la exige. Se guarda sin vuelta.', it: 'Se il server ne richiede una. Scritta senza ritorno.' },

  // ---- D639: Gastos de IA (Digital Central phase 4) ----
  'nav.aiSpend': {
    pt: 'Gastos de IA',
    en: 'AI spend',
    es: 'Gastos de IA',
    it: 'Spese IA',
  },
  'aispend.title': {
    pt: 'Gastos de IA',
    en: 'AI spend',
    es: 'Gastos de IA',
    it: 'Spese IA',
  },
  'aispend.subtitle': {
    pt: 'Quanto a IA da Central custou no mês, a projeção até o fim e o orçamento.',
    en: 'What the Central’s AI cost this month, the forecast to month end and the budget.',
    es: 'Cuánto costó la IA de la Central en el mes, la proyección hasta fin de mes y el presupuesto.',
    it: 'Quanto è costata l’IA della Centrale nel mese, la previsione a fine mese e il budget.',
  },
  'aispend.explain': {
    pt: 'O gasto vem de cada chamada de IA registrada, mês a mês em UTC. Ao chegar a 100% do orçamento, o assistente para de gastar e passa as conversas para pessoas, nunca gasta além do limite. O orçamento se define em Atendimento e IA.',
    en: 'Spend comes from every recorded AI call, month by month in UTC. At 100% of the budget the assistant stops spending and hands conversations to people; it never goes over the limit. The budget is set in Support and AI.',
    es: 'El gasto sale de cada llamada de IA registrada, mes a mes en UTC. Al llegar al 100% del presupuesto, el asistente deja de gastar y pasa las conversaciones a personas; nunca gasta más allá del límite. El presupuesto se define en Atención e IA.',
    it: 'La spesa deriva da ogni chiamata IA registrata, mese per mese in UTC. Al 100% del budget l’assistente smette di spendere e passa le conversazioni alle persone; non supera mai il limite. Il budget si definisce in Assistenza e IA.',
  },
  'aispend.noPermission': {
    pt: 'Seu acesso não inclui os gastos de IA.',
    en: 'Your access does not include AI spend.',
    es: 'Tu acceso no incluye los gastos de IA.',
    it: 'Il tuo accesso non include le spese IA.',
  },
  'aispend.loadFailed': {
    pt: 'Não foi possível ler os gastos de IA: {reason}',
    en: 'AI spend could not be read: {reason}',
    es: 'No se pudo leer los gastos de IA: {reason}',
    it: 'Impossibile leggere le spese IA: {reason}',
  },
  'aispend.month': {
    pt: 'Mês',
    en: 'Month',
    es: 'Mes',
    it: 'Mese',
  },
  'aispend.empty': {
    pt: 'Nenhuma chamada de IA neste mês.',
    en: 'No AI call this month.',
    es: 'Ninguna llamada de IA este mes.',
    it: 'Nessuna chiamata IA questo mese.',
  },
  'aispend.none': {
    pt: 'Sem indicação',
    en: 'Not specified',
    es: 'Sin indicación',
    it: 'Non indicato',
  },
  'aispend.scope.platform': {
    pt: 'Plataforma',
    en: 'Platform',
    es: 'Plataforma',
    it: 'Piattaforma',
  },
  'aispend.totals.title': {
    pt: 'No mês',
    en: 'This month',
    es: 'En el mes',
    it: 'Nel mese',
  },
  'aispend.totals.days': {
    pt: '{elapsed} de {total} dias do mês.',
    en: '{elapsed} of {total} days of the month.',
    es: '{elapsed} de {total} días del mes.',
    it: '{elapsed} di {total} giorni del mese.',
  },
  'aispend.stat.spent': {
    pt: 'Gasto no mês ({currency})',
    en: 'Spend this month ({currency})',
    es: 'Gasto en el mes ({currency})',
    it: 'Spesa nel mese ({currency})',
  },
  'aispend.stat.calls': {
    pt: '{calls} chamadas, {errors} com erro',
    en: '{calls} calls, {errors} with errors',
    es: '{calls} llamadas, {errors} con error',
    it: '{calls} chiamate, {errors} con errore',
  },
  'aispend.stat.forecast': {
    pt: 'Projeção do mês ({currency})',
    en: 'Month forecast ({currency})',
    es: 'Proyección del mes ({currency})',
    it: 'Previsione del mese ({currency})',
  },
  'aispend.stat.forecast.hint': {
    pt: 'No ritmo atual até o fim do mês',
    en: 'At the current pace to month end',
    es: 'Al ritmo actual hasta fin de mes',
    it: 'Al ritmo attuale fino a fine mese',
  },
  'aispend.budgets.title': {
    pt: 'Orçamentos',
    en: 'Budgets',
    es: 'Presupuestos',
    it: 'Budget',
  },
  'aispend.budgets.hint': {
    pt: 'Cada orçamento mensal contra o gasto, com alertas. A plataforma vale onde o país não tem o seu.',
    en: 'Each monthly budget against the spend, with alerts. The platform’s applies where a country has none of its own.',
    es: 'Cada presupuesto mensual frente al gasto, con alertas. El de la plataforma vale donde el país no tiene el suyo.',
    it: 'Ogni budget mensile rispetto alla spesa, con avvisi. Quello della piattaforma vale dove il paese non ha il proprio.',
  },
  'aispend.budgets.none': {
    pt: 'Nenhum orçamento para comparar.',
    en: 'No budget to compare.',
    es: 'Ningún presupuesto para comparar.',
    it: 'Nessun budget da confrontare.',
  },
  'aispend.budgets.used': {
    pt: 'Orçamento usado',
    en: 'Budget used',
    es: 'Presupuesto usado',
    it: 'Budget usato',
  },
  'aispend.budgets.line': {
    pt: 'Gasto {spent} de {budget}',
    en: 'Spent {spent} of {budget}',
    es: 'Gasto {spent} de {budget}',
    it: 'Speso {spent} di {budget}',
  },
  'aispend.budgets.projected': {
    pt: 'Projeção do mês: {projected} ({percent} do orçamento)',
    en: 'Month forecast: {projected} ({percent} of the budget)',
    es: 'Proyección del mes: {projected} ({percent} del presupuesto)',
    it: 'Previsione del mese: {projected} ({percent} del budget)',
  },
  'aispend.budgets.alerts': {
    pt: 'Alertas em {percents}',
    en: 'Alerts at {percents}',
    es: 'Alertas en {percents}',
    it: 'Avvisi al {percents}',
  },
  'aispend.budgets.willExceed': {
    pt: 'No ritmo atual o orçamento acaba antes do fim do mês.',
    en: 'At the current pace the budget runs out before month end.',
    es: 'Al ritmo actual el presupuesto se agota antes de fin de mes.',
    it: 'Al ritmo attuale il budget finisce prima di fine mese.',
  },
  'aispend.budgets.exceeded': {
    pt: 'O orçamento acabou: o assistente não faz novas chamadas e passa as conversas para pessoas até o mês virar ou o orçamento subir.',
    en: 'The budget is used up: the assistant makes no new calls and hands conversations to people until the month turns or the budget is raised.',
    es: 'El presupuesto se agotó: el asistente no hace nuevas llamadas y pasa las conversaciones a personas hasta que cambie el mes o suba el presupuesto.',
    it: 'Il budget è esaurito: l’assistente non fa nuove chiamate e passa le conversazioni alle persone finché cambia il mese o il budget sale.',
  },
  'aispend.budgets.unset': {
    pt: 'Sem orçamento definido: o gasto é acompanhado, mas não há teto que pare o assistente.',
    en: 'No budget set: spend is tracked, but there is no ceiling to stop the assistant.',
    es: 'Sin presupuesto definido: el gasto se sigue, pero no hay tope que detenga al asistente.',
    it: 'Nessun budget definito: la spesa è seguita, ma non c’è un tetto che fermi l’assistente.',
  },
  'aispend.budgets.missingRate': {
    pt: 'Falta a cotação {pair} para comparar o gasto com o orçamento. Até registrá-la, o assistente passa as conversas para pessoas: gasto que não dá para medir não é gasto livre.',
    en: 'The {pair} exchange rate is missing to compare spend with the budget. Until it is recorded the assistant hands conversations to people: spend that cannot be measured is not free spend.',
    es: 'Falta la cotización {pair} para comparar el gasto con el presupuesto. Hasta registrarla, el asistente pasa las conversaciones a personas: un gasto que no se puede medir no es gasto libre.',
    it: 'Manca il cambio {pair} per confrontare la spesa con il budget. Finché non viene registrato, l’assistente passa le conversazioni alle persone: una spesa che non si può misurare non è spesa libera.',
  },
  'aispend.budgets.invalid': {
    pt: 'Este orçamento não pôde ser lido: o assistente passa as conversas para pessoas até ele ser corrigido em Configurações.',
    en: 'This budget could not be read: the assistant hands conversations to people until it is fixed in Settings.',
    es: 'Este presupuesto no se pudo leer: el asistente pasa las conversaciones a personas hasta que se corrija en Configuración.',
    it: 'Questo budget non si è potuto leggere: l’assistente passa le conversazioni alle persone finché non viene corretto in Impostazioni.',
  },
  'aispend.budgets.unknownCost': {
    pt: '{calls} chamadas bem-sucedidas vieram sem custo do provedor: o gasto real pode ser maior que o mostrado, e o assistente passa as conversas para pessoas até isso ser resolvido.',
    en: '{calls} successful calls came back with no cost from the provider: the real spend may be higher than shown, and the assistant hands conversations to people until this is resolved.',
    es: '{calls} llamadas exitosas llegaron sin costo del proveedor: el gasto real puede ser mayor que el mostrado, y el asistente pasa las conversaciones a personas hasta resolverlo.',
    it: '{calls} chiamate riuscite sono arrivate senza costo dal provider: la spesa reale può essere maggiore di quella mostrata e l’assistente passa le conversazioni alle persone finché non si risolve.',
  },
  'aispend.alerts.title': {
    pt: 'Limites atingidos neste mês',
    en: 'Thresholds crossed this month',
    es: 'Límites alcanzados este mes',
    it: 'Soglie raggiunte questo mese',
  },
  'aispend.alerts.hint': {
    pt: 'Cada limite é registrado uma só vez, quando o gasto o atinge. Em 100% o assistente passa as conversas para pessoas.',
    en: 'Each threshold is recorded once, when the spend reaches it. At 100% the assistant hands conversations to people.',
    es: 'Cada límite se registra una sola vez, cuando el gasto lo alcanza. En el 100% el asistente pasa las conversaciones a personas.',
    it: 'Ogni soglia viene registrata una sola volta, quando la spesa la raggiunge. Al 100% l’assistente passa le conversazioni alle persone.',
  },
  'aispend.failures.title': {
    pt: 'Por que as chamadas falharam',
    en: 'Why calls failed',
    es: 'Por qué fallaron las llamadas',
    it: 'Perché le chiamate non sono riuscite',
  },
  'aispend.failures.hint': {
    pt: 'Chamadas sem resposta neste mês, por uso e motivo. A última mensagem é a do próprio provedor.',
    en: 'Calls with no answer this month, by use and reason. The last message is the provider’s own.',
    es: 'Llamadas sin respuesta este mes, por uso y motivo. El último mensaje es el del propio proveedor.',
    it: 'Chiamate senza risposta in questo mese, per uso e motivo. L’ultimo messaggio è quello del provider.',
  },
  'aispend.failures.col.reason': {
    pt: 'Motivo',
    en: 'Reason',
    es: 'Motivo',
    it: 'Motivo',
  },
  'aispend.failures.col.last': {
    pt: 'Última vez',
    en: 'Last time',
    es: 'Última vez',
    it: 'Ultima volta',
  },
  'aispend.failures.col.detail': {
    pt: 'Última mensagem do provedor',
    en: 'Provider’s last message',
    es: 'Último mensaje del proveedor',
    it: 'Ultimo messaggio del provider',
  },
  'aispend.failures.credits': {
    pt: 'O OpenRouter recusou chamadas por falta de crédito (última vez: {at}). Enquanto isso, o assistente passa as conversas para pessoas. Adicione crédito na conta do OpenRouter ou aumente o limite da chave.',
    en: 'OpenRouter refused calls for lack of credit (last time: {at}). Until this is fixed, the assistant hands conversations to people. Add credit to the OpenRouter account or raise the key’s limit.',
    es: 'OpenRouter rechazó llamadas por falta de crédito (última vez: {at}). Mientras tanto, el asistente pasa las conversaciones a personas. Agregue crédito a la cuenta de OpenRouter o aumente el límite de la clave.',
    it: 'OpenRouter ha rifiutato chiamate per mancanza di credito (ultima volta: {at}). Nel frattempo l’assistente passa le conversazioni alle persone. Aggiungi credito all’account OpenRouter o aumenta il limite della chiave.',
  },
  'aispend.failures.key': {
    pt: 'O OpenRouter recusou a chave (última vez: {at}). Enquanto isso, o assistente passa as conversas para pessoas. Confira a chave em Integrações.',
    en: 'OpenRouter refused the key (last time: {at}). Until this is fixed, the assistant hands conversations to people. Check the key in Integrations.',
    es: 'OpenRouter rechazó la clave (última vez: {at}). Mientras tanto, el asistente pasa las conversaciones a personas. Revise la clave en Integraciones.',
    it: 'OpenRouter ha rifiutato la chiave (ultima volta: {at}). Nel frattempo l’assistente passa le conversazioni alle persone. Controlla la chiave in Integrazioni.',
  },
  'aispend.alerts.crossed': {
    pt: '{percent}% atingido',
    en: '{percent}% reached',
    es: '{percent}% alcanzado',
    it: '{percent}% raggiunto',
  },
  'aispend.resolved.title': {
    pt: 'Custo por conversa resolvida',
    en: 'Cost per resolved conversation',
    es: 'Costo por conversación resuelta',
    it: 'Costo per conversazione risolta',
  },
  'aispend.resolved.hint': {
    pt: 'Só conta a conversa que o Vello resolveu sozinha: terminou resolvida, nunca foi passada a uma pessoa e nenhuma sugestão foi trabalhada por um atendente.',
    en: 'Only conversations Vello resolved on its own: they ended resolved, were never handed to a person and no draft was worked by an agent.',
    es: 'Solo cuenta la conversación que Vello resolvió sola: terminó resuelta, nunca pasó a una persona y ninguna sugerencia fue trabajada por un agente.',
    it: 'Conta solo la conversazione che Vello ha risolto da sola: finita risolta, mai passata a una persona e nessun suggerimento lavorato da un operatore.',
  },
  'aispend.resolved.empty': {
    pt: 'Nenhuma conversa resolvida sozinha neste mês.',
    en: 'No conversation resolved alone this month.',
    es: 'Ninguna conversación resuelta sola este mes.',
    it: 'Nessuna conversazione risolta da sola questo mese.',
  },
  'aispend.resolved.col.conversations': {
    pt: 'Conversas resolvidas',
    en: 'Resolved conversations',
    es: 'Conversaciones resueltas',
    it: 'Conversazioni risolte',
  },
  'aispend.resolved.col.per': {
    pt: 'Custo por conversa',
    en: 'Cost per conversation',
    es: 'Costo por conversación',
    it: 'Costo per conversazione',
  },
  'aispend.col.cost': {
    pt: 'Custo',
    en: 'Cost',
    es: 'Costo',
    it: 'Costo',
  },
  'aispend.col.share': {
    pt: 'Parte',
    en: 'Share',
    es: 'Parte',
    it: 'Quota',
  },
  'aispend.col.calls': {
    pt: 'Chamadas',
    en: 'Calls',
    es: 'Llamadas',
    it: 'Chiamate',
  },
  'aispend.col.perCall': {
    pt: 'Por chamada',
    en: 'Per call',
    es: 'Por llamada',
    it: 'Per chiamata',
  },
  'aispend.col.errors': {
    pt: 'Com erro',
    en: 'With errors',
    es: 'Con error',
    it: 'Con errore',
  },
  'aispend.col.tokens': {
    pt: 'Tokens',
    en: 'Tokens',
    es: 'Tokens',
    it: 'Token',
  },
  'aispend.group.purpose': {
    pt: 'Por finalidade',
    en: 'By purpose',
    es: 'Por finalidad',
    it: 'Per finalità',
  },
  'aispend.group.purpose.hint': {
    pt: 'Para que a IA foi chamada.',
    en: 'What the AI was called for.',
    es: 'Para qué se llamó a la IA.',
    it: 'Per cosa è stata chiamata l’IA.',
  },
  'aispend.group.model': {
    pt: 'Por modelo',
    en: 'By model',
    es: 'Por modelo',
    it: 'Per modello',
  },
  'aispend.group.model.hint': {
    pt: 'Qual modelo respondeu, inclusive quando o reserva assumiu.',
    en: 'Which model answered, including when the fallback took over.',
    es: 'Qué modelo respondió, incluso cuando asumió el de reserva.',
    it: 'Quale modello ha risposto, anche quando è subentrato quello di riserva.',
  },
  'aispend.group.country': {
    pt: 'Por país',
    en: 'By country',
    es: 'Por país',
    it: 'Per paese',
  },
  'aispend.group.country.hint': {
    pt: 'O país da conversa que gerou a chamada.',
    en: 'The country of the conversation that caused the call.',
    es: 'El país de la conversación que originó la llamada.',
    it: 'Il paese della conversazione che ha causato la chiamata.',
  },
  'aispend.group.channel': {
    pt: 'Por canal',
    en: 'By channel',
    es: 'Por canal',
    it: 'Per canale',
  },
  'aispend.group.channel.hint': {
    pt: 'O canal por onde o cliente falou.',
    en: 'The channel the customer used.',
    es: 'El canal por donde habló el cliente.',
    it: 'Il canale usato dal cliente.',
  },
  'aispend.daily.title': {
    pt: 'Dia a dia',
    en: 'Day by day',
    es: 'Día a día',
    it: 'Giorno per giorno',
  },
  'aispend.daily.col.date': {
    pt: 'Dia (UTC)',
    en: 'Day (UTC)',
    es: 'Día (UTC)',
    it: 'Giorno (UTC)',
  },
  'aispend.fx.title': {
    pt: 'Cotações',
    en: 'Exchange rates',
    es: 'Cotizaciones',
    it: 'Cambi',
  },
  'aispend.fx.hint': {
    pt: 'O provedor cobra em uma moeda e o orçamento pode estar em outra. A comparação usa a cotação registrada aqui; sem ela o orçamento não pode ser comparado. Cada cotação é um registro novo, nunca se apaga.',
    en: 'The provider bills in one currency and the budget may be in another. The comparison uses the rate recorded here; without it the budget cannot be compared. Every rate is a new record, never erased.',
    es: 'El proveedor cobra en una moneda y el presupuesto puede estar en otra. La comparación usa la cotización registrada aquí; sin ella el presupuesto no se puede comparar. Cada cotización es un registro nuevo, nunca se borra.',
    it: 'Il provider fattura in una valuta e il budget può essere in un’altra. Il confronto usa il cambio registrato qui; senza, il budget non è confrontabile. Ogni cambio è una nuova registrazione, mai cancellata.',
  },
  'aispend.fx.empty': {
    pt: 'Nenhuma cotação registrada.',
    en: 'No exchange rate recorded.',
    es: 'Ninguna cotización registrada.',
    it: 'Nessun cambio registrato.',
  },
  'aispend.fx.col.pair': {
    pt: 'Moedas',
    en: 'Currencies',
    es: 'Monedas',
    it: 'Valute',
  },
  'aispend.fx.col.rate': {
    pt: 'Cotação',
    en: 'Rate',
    es: 'Cotización',
    it: 'Cambio',
  },
  'aispend.fx.col.effective': {
    pt: 'Vale desde',
    en: 'Valid from',
    es: 'Vale desde',
    it: 'Valido dal',
  },
  'aispend.fx.col.by': {
    pt: 'Registrada por',
    en: 'Recorded by',
    es: 'Registrada por',
    it: 'Registrato da',
  },
  'aispend.fx.form.title': {
    pt: 'Registrar cotação',
    en: 'Record a rate',
    es: 'Registrar cotización',
    it: 'Registra un cambio',
  },
  'aispend.fx.suggest': {
    pt: 'Registrar {from} → {to}',
    en: 'Record {from} → {to}',
    es: 'Registrar {from} → {to}',
    it: 'Registra {from} → {to}',
  },
  'aispend.fx.form.from': {
    pt: 'De (moeda)',
    en: 'From (currency)',
    es: 'De (moneda)',
    it: 'Da (valuta)',
  },
  'aispend.fx.form.to': {
    pt: 'Para (moeda)',
    en: 'To (currency)',
    es: 'A (moneda)',
    it: 'A (valuta)',
  },
  'aispend.fx.form.rate': {
    pt: 'Cotação',
    en: 'Rate',
    es: 'Cotización',
    it: 'Cambio',
  },
  'aispend.fx.form.rate.hint': {
    pt: 'Quanto vale 1 {from} em {to}. Até 6 casas decimais.',
    en: 'How much 1 {from} is worth in {to}. Up to 6 decimals.',
    es: 'Cuánto vale 1 {from} en {to}. Hasta 6 decimales.',
    it: 'Quanto vale 1 {from} in {to}. Fino a 6 decimali.',
  },
  'aispend.fx.form.save': {
    pt: 'Registrar cotação',
    en: 'Record rate',
    es: 'Registrar cotización',
    it: 'Registra cambio',
  },
  'aispend.fx.invalidPair': {
    pt: 'Informe duas moedas diferentes, com 3 letras cada.',
    en: 'Enter two different currencies, 3 letters each.',
    es: 'Indica dos monedas distintas, de 3 letras cada una.',
    it: 'Indica due valute diverse, di 3 lettere ciascuna.',
  },
  'aispend.fx.invalidRate': {
    pt: 'A cotação deve ser um número maior que zero, com até 6 casas decimais.',
    en: 'The rate must be a number above zero with up to 6 decimals.',
    es: 'La cotización debe ser un número mayor que cero, con hasta 6 decimales.',
    it: 'Il cambio deve essere un numero maggiore di zero, con fino a 6 decimali.',
  },
  'aispend.fx.saved': {
    pt: 'Cotação {from} → {to} registrada.',
    en: 'Rate {from} → {to} recorded.',
    es: 'Cotización {from} → {to} registrada.',
    it: 'Cambio {from} → {to} registrato.',
  },

  // ---- D639: Vello autonomy editor ----
  'centralai.autonomy.explain': {
    pt: 'Todo assunto começa em sugestão: o Vello escreve e uma pessoa envia. Só se sobe de nível depois que as simulações do assunto passam, e quem aprova é outra pessoa. Desligar um canal ou um assunto vale na hora de aprovar e nunca pode ser desfeito por um país.',
    en: 'Every topic starts as a suggestion: Vello drafts and a person sends. A level goes up only after the topic’s simulations pass, and a different person approves. Switching a channel or topic off holds as soon as it is approved and a country can never undo it.',
    es: 'Todo tema empieza como sugerencia: Vello redacta y una persona envía. Solo se sube de nivel cuando las simulaciones del tema pasan, y aprueba otra persona. Apagar un canal o un tema vale al aprobarse y un país nunca puede deshacerlo.',
    it: 'Ogni argomento parte come suggerimento: Vello scrive e una persona invia. Si sale di livello solo dopo che le simulazioni dell’argomento passano, e approva un’altra persona. Spegnere un canale o un argomento vale all’approvazione e un paese non può mai annullarlo.',
  },
  'centralai.autonomy.loading': {
    pt: 'Lendo o que o Vello sabe fazer…',
    en: 'Reading what Vello can do…',
    es: 'Leyendo lo que Vello sabe hacer…',
    it: 'Lettura di ciò che Vello sa fare…',
  },
  'centralai.autonomy.unreadable': {
    pt: 'Não foi possível ler os assuntos do Vello. Sem essa lista o formulário não oferece subir de nível: só o liga/desliga e o nível padrão em sugestão.',
    en: 'Vello’s topics could not be read. Without that list the form offers no promotion: only the switch and the default level as a suggestion.',
    es: 'No se pudieron leer los temas de Vello. Sin esa lista el formulario no ofrece subir de nivel: solo el interruptor y el nivel predeterminado como sugerencia.',
    it: 'Impossibile leggere gli argomenti di Vello. Senza quell’elenco il modulo non offre promozioni: solo l’interruttore e il livello predefinito come suggerimento.',
  },
  'centralai.autonomy.enabled': {
    pt: 'Vello ligada',
    en: 'Vello on',
    es: 'Vello encendida',
    it: 'Vello attiva',
  },
  'centralai.autonomy.enabled.hint': {
    pt: 'Desligado, o Vello não responde nem sugere: as conversas ficam só com as pessoas. Um país só pode desligar, nunca religar o que a plataforma desligou.',
    en: 'When off, Vello neither replies nor suggests: conversations stay with people. A country can switch off but never back on what the platform switched off.',
    es: 'Apagada, Vello no responde ni sugiere: las conversaciones quedan solo con las personas. Un país solo puede apagar, nunca volver a encender lo que la plataforma apagó.',
    it: 'Spenta, Vello non risponde né suggerisce: le conversazioni restano solo alle persone. Un paese può solo spegnere, mai riaccendere ciò che la piattaforma ha spento.',
  },
  'centralai.autonomy.default': {
    pt: 'Nível dos assuntos sem regra própria',
    en: 'Level for topics with no rule of their own',
    es: 'Nivel de los temas sin regla propia',
    it: 'Livello degli argomenti senza regola propria',
  },
  'centralai.autonomy.default.hint': {
    pt: 'O padrão é sugestão. Acima disso só vale para assuntos com simulação aprovada.',
    en: 'The default is suggestion. Anything above it only applies to topics whose simulation passes.',
    es: 'El valor predeterminado es sugerencia. Por encima solo vale para temas con simulación aprobada.',
    it: 'Il predefinito è suggerimento. Oltre vale solo per argomenti con simulazione approvata.',
  },
  'centralai.autonomy.intents': {
    pt: 'Nível por assunto',
    en: 'Level by topic',
    es: 'Nivel por tema',
    it: 'Livello per argomento',
  },
  'centralai.autonomy.follows': {
    pt: 'Segue o nível padrão',
    en: 'Follows the default level',
    es: 'Sigue el nivel predeterminado',
    it: 'Segue il livello predefinito',
  },
  'centralai.autonomy.notPromotable': {
    pt: 'Sem simulação aprovada: só sugestão.',
    en: 'No passing simulation: suggestion only.',
    es: 'Sin simulación aprobada: solo sugerencia.',
    it: 'Nessuna simulazione approvata: solo suggerimento.',
  },
  'centralai.autonomy.awaitsSurface': {
    pt: 'Ainda não pode ser promovido: depende de uma tela do app que ainda não foi entregue. Por enquanto, só sugestão.',
    en: 'Cannot be promoted yet: it depends on an app screen that is not delivered. For now, suggestion only.',
    es: 'Aún no se puede promover: depende de una pantalla de la app que todavía no se entregó. Por ahora, solo sugerencia.',
    it: 'Non può ancora essere promosso: dipende da una schermata dell’app non ancora consegnata. Per ora, solo suggerimento.',
  },
  'centralai.autonomy.channels': {
    pt: 'Desligar por canal',
    en: 'Switch off by channel',
    es: 'Apagar por canal',
    it: 'Spegni per canale',
  },
  'centralai.autonomy.channels.hint': {
    pt: 'Nos canais marcados o Vello nunca passa de sugestão, qualquer que seja o nível.',
    en: 'On the ticked channels Vello never goes beyond a suggestion, whatever the level.',
    es: 'En los canales marcados Vello nunca pasa de sugerencia, sea cual sea el nivel.',
    it: 'Sui canali selezionati Vello non va mai oltre il suggerimento, qualunque sia il livello.',
  },
  'centralai.autonomy.killIntents': {
    pt: 'Desligar por assunto',
    en: 'Switch off by topic',
    es: 'Apagar por tema',
    it: 'Spegni per argomento',
  },
  'centralai.autonomy.killIntents.hint': {
    pt: 'Nos assuntos marcados o Vello nunca passa de sugestão, em nenhum canal.',
    en: 'On the ticked topics Vello never goes beyond a suggestion, on any channel.',
    es: 'En los temas marcados Vello nunca pasa de sugerencia, en ningún canal.',
    it: 'Sugli argomenti selezionati Vello non va mai oltre il suggerimento, su nessun canale.',
  },
  'centralai.summary.autonomy.on': {
    pt: 'Vello ligada. Nível padrão: {level}',
    en: 'Vello on. Default level: {level}',
    es: 'Vello encendida. Nivel predeterminado: {level}',
    it: 'Vello attiva. Livello predefinito: {level}',
  },
  'centralai.summary.autonomy.off': {
    pt: 'Vello desligada: as conversas ficam com as pessoas',
    en: 'Vello off: conversations stay with people',
    es: 'Vello apagada: las conversaciones quedan con las personas',
    it: 'Vello spenta: le conversazioni restano alle persone',
  },
  'centralai.summary.autonomy.intent': {
    pt: '{intent}: {level}',
    en: '{intent}: {level}',
    es: '{intent}: {level}',
    it: '{intent}: {level}',
  },
  'centralai.summary.autonomy.killed': {
    pt: 'Desligado, só sugestão: {items}',
    en: 'Switched off, suggestion only: {items}',
    es: 'Apagado, solo sugerencia: {items}',
    it: 'Spento, solo suggerimento: {items}',
  },
};
